import { skipToken } from '@reduxjs/toolkit/query';
import { SEND_SHEET } from 'components/modals/sideSheet';
import { useModal } from 'components/modals/useModal';
import { useCallback, useMemo } from 'react';
import { useGetInvoiceSendValidationQuery } from 'store/api/invoices';
import { FeatureFlag } from 'types/features';
import { InvoiceStatusType, InvoiceType } from 'types/invoices';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import InvoiceSendValidationModal, { InvoiceValidationItem } from '../InvoiceSendValidationModal';

interface InvoiceBase {
    id: number;
    invoiceNumber: string;
    invoiceTypeCode: string;
    invoiceStatusTypeCode: string;
}

const getInvoiceTypeFeatureFlag = (invoiceTypeCode: string): FeatureFlag | null => {
    switch (invoiceTypeCode) {
        case InvoiceType.DEBIT:
            return FeatureFlag.INVOICE_DATES_CONTROL_MODAL_DEBIT;
        case InvoiceType.CREDIT:
            return FeatureFlag.INVOICE_DATES_CONTROL_MODAL_CREDIT;
        case InvoiceType.PREPAYMENT:
            return FeatureFlag.INVOICE_DATES_CONTROL_MODAL_PREPAYMENT;
        case InvoiceType.CORRECTION:
            return FeatureFlag.INVOICE_DATES_CONTROL_MODAL_CORRECTION;
        default:
            return null;
    }
};

export const useInvoiceSendValidation = <T extends InvoiceBase>(invoices: T[]) => {
    const isGeneralFeatureEnabled = useIsFeatureEnabled(FeatureFlag.INVOICE_DATES_CONTROL_MODAL);
    const isDebitFeatureEnabled = useIsFeatureEnabled(FeatureFlag.INVOICE_DATES_CONTROL_MODAL_DEBIT);
    const isCreditFeatureEnabled = useIsFeatureEnabled(FeatureFlag.INVOICE_DATES_CONTROL_MODAL_CREDIT);
    const isPrepaymentFeatureEnabled = useIsFeatureEnabled(FeatureFlag.INVOICE_DATES_CONTROL_MODAL_PREPAYMENT);
    const isCorrectionFeatureEnabled = useIsFeatureEnabled(FeatureFlag.INVOICE_DATES_CONTROL_MODAL_CORRECTION);
    const { openModal, closeModal } = useModal();

    // Filter invoices that should be validated based on feature flags and status
    const invoicesToValidate = useMemo(() => {
        return invoices.filter((invoice) => {
            if (invoice.invoiceStatusTypeCode !== InvoiceStatusType.FINALIZED) {
                return false;
            }

            const invoiceTypeFeatureFlag = getInvoiceTypeFeatureFlag(invoice.invoiceTypeCode);
            if (!invoiceTypeFeatureFlag) {
                return false;
            }

            let isInvoiceTypeFeatureEnabled = false;
            switch (invoice.invoiceTypeCode) {
                case InvoiceType.DEBIT:
                    isInvoiceTypeFeatureEnabled = isDebitFeatureEnabled;
                    break;
                case InvoiceType.CREDIT:
                    isInvoiceTypeFeatureEnabled = isCreditFeatureEnabled;
                    break;
                case InvoiceType.PREPAYMENT:
                    isInvoiceTypeFeatureEnabled = isPrepaymentFeatureEnabled;
                    break;
                case InvoiceType.CORRECTION:
                    isInvoiceTypeFeatureEnabled = isCorrectionFeatureEnabled;
                    break;
            }

            return isGeneralFeatureEnabled && isInvoiceTypeFeatureEnabled;
        });
    }, [
        invoices,
        isGeneralFeatureEnabled,
        isDebitFeatureEnabled,
        isCreditFeatureEnabled,
        isPrepaymentFeatureEnabled,
        isCorrectionFeatureEnabled,
    ]);

    // Fetch validation data for all eligible invoices in a single request
    const invoiceIds = useMemo(() => invoicesToValidate.map((inv) => inv.id), [invoicesToValidate]);
    const { data: validationDataArray } = useGetInvoiceSendValidationQuery(
        invoiceIds.length > 0 ? invoiceIds : skipToken
    );

    const invoicesWithValidation: InvoiceValidationItem[] = useMemo(() => {
        if (!validationDataArray) {
            return [];
        }

        // Create a map for quick lookup
        const validationMap = new Map(validationDataArray.map((v) => [v.invoiceId, v]));

        return invoicesToValidate
            .filter((invoice) => validationMap.has(invoice.id))
            .map((invoice) => ({
                invoiceId: invoice.id,
                invoiceNumber: invoice.invoiceNumber,
                validationData: validationMap.get(invoice.id)!,
            }));
    }, [invoicesToValidate, validationDataArray]);

    const validateAndProceed = useCallback(
        async (onProceed: () => void): Promise<void> => {
            if (!isGeneralFeatureEnabled) {
                onProceed();
                return;
            }

            // Check if any invoice has warnings
            const hasAnyWarnings = invoicesWithValidation.some((invoice) => {
                const data = invoice.validationData;
                return (
                    data &&
                    (data.hasUnusedPrepayment ||
                        data.hasDueDateWarning ||
                        data.hasInvoiceDateWarning ||
                        data.hasLinkedPrepaymentPaidAfterInvoiceDate)
                );
            });

            if (hasAnyWarnings) {
                openModal(
                    <InvoiceSendValidationModal
                        onCancel={closeModal}
                        onConfirm={() => {
                            closeModal();
                            onProceed();
                        }}
                        invoices={invoicesWithValidation}
                    />,
                    closeModal,
                    undefined,
                    undefined,
                    SEND_SHEET
                );
            } else {
                onProceed();
            }
        },
        [isGeneralFeatureEnabled, invoicesWithValidation, openModal, closeModal]
    );

    return { validateAndProceed };
};
